import { StateEffect, StateField, type EditorState, type Range } from "@codemirror/state";
import { Decoration, EditorView, WidgetType, type DecorationSet } from "@codemirror/view";

import { renderInline } from "./markdown.ts";

/**
 * A note shown under a range of lines, with the lines marked, as a guide
 * points at code. The range follows edits; the note stays until it is
 * closed or another note replaces it.
 */

interface Note {
    from: number;
    to: number;
    text: string;
}

const setNote = StateEffect.define<Note | null>();

class NoteWidget extends WidgetType {
    readonly text: string;

    constructor(text: string) {
        super();
        this.text = text;
    }

    override eq(other: NoteWidget): boolean {
        return other.text === this.text;
    }

    toDOM(view: EditorView): HTMLElement {
        const note = document.createElement("div");
        note.className = "wb-code-note";
        note.setAttribute("role", "note");
        const text = document.createElement("div");
        text.append(renderInline(this.text));
        const close = document.createElement("button");
        close.type = "button";
        close.className = "wb-code-note-close codicon codicon-close";
        close.setAttribute("aria-label", "Close Note");
        close.title = "Close Note";
        close.addEventListener("click", () => view.dispatch({ effects: setNote.of(null) }));
        note.append(text, close);
        return note;
    }

    override ignoreEvent(): boolean {
        return true;
    }
}

function decorations(state: EditorState, note: Note): DecorationSet {
    const first = state.doc.lineAt(note.from).number;
    const last = state.doc.lineAt(note.to).number;
    const marks: Range<Decoration>[] = [];
    for (let line = first; line <= last; line++)
        marks.push(Decoration.line({ class: "wb-code-note-line" }).range(state.doc.line(line).from));
    const end = state.doc.line(last).to;
    if (note.text !== "")
        marks.push(Decoration.widget({ widget: new NoteWidget(note.text), block: true, side: 1 }).range(end));
    return Decoration.set(marks, true);
}

export const codeNotes = StateField.define<{ note: Note | null; decorations: DecorationSet }>({
    create: () => ({ note: null, decorations: Decoration.none }),
    update(value, tr) {
        const effect = tr.effects.findLast((e) => e.is(setNote));
        if (effect === undefined && !tr.docChanged) return value;
        const mapped = value.note && {
            ...value.note,
            from: tr.changes.mapPos(value.note.from, -1),
            to: tr.changes.mapPos(value.note.to, 1),
        };
        const note = effect === undefined ? mapped : (effect.value as Note | null);
        return { note, decorations: note === null ? Decoration.none : decorations(tr.state, note) };
    },
    provide: (field) => EditorView.decorations.from(field, (value) => value.decorations),
});

/** Mark lines `first` to `last` (1-based, clamped to the file) and show `text` under them; empty text only marks. */
export function showNote(view: EditorView, first: number, last: number, text: string): void {
    const doc = view.state.doc;
    const from = doc.line(Math.min(Math.max(first, 1), doc.lines)).from;
    const to = doc.line(Math.min(Math.max(last, first, 1), doc.lines)).to;
    view.dispatch({
        effects: [setNote.of({ from, to, text }), EditorView.scrollIntoView(from, { y: "center" })],
        selection: { anchor: from, head: to },
    });
}

export function clearNote(view: EditorView): void {
    if (view.state.field(codeNotes).note !== null) view.dispatch({ effects: setNote.of(null) });
}
